import { useEffect, useRef, useState } from "react";

import { Server } from "@wajeez/wajeez.microservices";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { OnItemPress } from "@modules/content/hooks/useComponentData";
import { Card } from "@ui/card";
import CircularPrgressBar from "@components/items/display/CircularPrgressBar";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { ItemContentOutputWithProgress } from "@lib/stores/content/types";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";

import { FullBookChaptersSkeleton } from "./FullBookChaptersSkeleton";

export interface FullBookChaptersParameters {
  title: string | null;
}

export interface FullBookChapterItemParameters {
  indexTitle: string | null;
  fromPage: string | null;
  toPage: string | null;
  currentProgress: number | null;
  isCompleted: boolean | null;
}

type ItemParams = {
  id: string | undefined;
  title: string | undefined;
} & {
  [K in keyof FullBookChapterItemParameters]?:
    | FullBookChapterItemParameters[K]
    | undefined;
};

interface FullBookChaptersProps {
  onItemPress: OnItemPress<ItemParams>;
  contents: Server.ContentEntityOutput[] | undefined;
  isLoading: boolean;
  parameterValues: FullBookChaptersParameters;
  items: ItemParams[];
  isLockedContent: boolean | undefined;
}

const FullBookChapters = ({
  onItemPress,
  contents,
  isLoading,
  parameterValues,
  items,
  isLockedContent,
}: FullBookChaptersProps) => {
  const { title } = parameterValues;

  const chaptersContainerElementRef = useRef<HTMLDivElement>(null);

  const [chaptersWithOptimisticProgress, setChaptersWithOptimisticProgress] =
    useState<ItemContentOutputWithProgress[]>([]);
  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  const { chapters, playingChapterId } = useSelectedPlayerContent();
  const { duration, currentTime } = useAudioPlayer();

  useEffect(() => {
    setChaptersWithOptimisticProgress(
      chapters.map((c) => {
        return {
          ...c,
          progress: Number(((c.progress || 0) * 100).toFixed()),
        };
      })
    );
  }, [chapters, chapters.length]);

  useEffect(() => {
    setChaptersWithOptimisticProgress((chapters) => {
      return chapters.map((c) => {
        return {
          ...c,
          progress:
            duration && playingChapterId === c.id
              ? Number(((currentTime / duration) * 100).toFixed(0))
              : c.progress,
          isCompleted: c.isCompleted
            ? c.isCompleted
            : currentTime / duration > 0.95 && playingChapterId === c.id
              ? true
              : false,
        };
      });
    });
  }, [playingChapterId, currentTime, duration]);

  useEffect(() => {
    if (chaptersContainerElementRef.current && playingChapterId) {
      const chapterElements = chaptersContainerElementRef.current.children;

      for (let i = 0; i < chapterElements.length; i++) {
        const chapterElement = chapterElements[i] as HTMLDivElement;

        if (chapterElement.id === playingChapterId) {
          chapterElement.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [playingChapterId]);

  return (
    <div
      className={`cus-scrollbar-w-none relative w-full overflow-y-auto pb-16 ${(isLoadingVisible || isLoading) && "h-[220px]"}`}
    >
      <div
        className={`absolute top-0 w-full ltr:left-0 rtl:right-0 ${
          isLoadingVisible && "z-10 bg-white dark:bg-black"
        }`}
      >
        <FullBookChaptersLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="w-full">
          <div className="text-[20px] font-bold dark:text-white">{title}</div>
          <div
            ref={chaptersContainerElementRef}
            className="mt-4 flex w-full flex-col justify-center gap-4"
          >
            {items &&
              items.map((chapter, index) => {
                const chapterWithProgress = chaptersWithOptimisticProgress.find(
                  (c) => c.id === chapter.id
                );

                const currentChapter = chaptersWithOptimisticProgress.find(
                  (c) => c.id === playingChapterId
                );

                const isCurrentChapter =
                  currentChapter && currentChapter.id === chapter.id;

                const content = contents.find((c) => c.id === chapter.id);

                return (
                  <Card
                    key={index}
                    id={chapter.id}
                    className={`box-border flex flex-col gap-3 rounded-xl border bg-[#F3F4F7] dark:border-none dark:bg-[#1E344D] dark:text-white ${isLockedContent && "bg-white dark:bg-[#010D19]"} ${!isLockedContent && "cursor-pointer"} ${isCurrentChapter && "bg-primary text-white dark:bg-[#64A6E1] dark:text-[#0C0C0C]"}`}
                    onClick={() => {
                      !isLockedContent &&
                        content &&
                        onItemPress &&
                        onItemPress(content, chapter, {
                          isLocked: isLockedContent,
                        });
                    }}
                  >
                    <div className="flex w-full items-center justify-between px-3 pt-3">
                      <div
                        className={`text-[14px] font-medium ${isLockedContent && "text-[#E9E9E9] dark:text-[#2E2E2E]"}`}
                      >
                        {chapter.indexTitle}
                      </div>
                      <div>
                        <CircularPrgressBar
                          value={chapterWithProgress?.progress}
                          size={57}
                          strokeWidth={6}
                          className={`stroke-[#42B1BE3D]/25 dark:stroke-[#64A6E13D] ${isLockedContent && "stroke-[#E5E5E5] dark:stroke-[#232323]"} ${isCurrentChapter && "stroke-[#8DDFDA] dark:stroke-[#A3D4FF]"}`}
                          progressClassName={`stroke-[#42B1BE]  dark:stroke-[#64A6E1] ${isLockedContent && "stroke-[#E5E5E5] dark:stroke-[#232323]"} ${isCurrentChapter && "stroke-[#077784] dark:stroke-[#496789]"}`}
                          isCompleted={
                            chapterWithProgress?.isCompleted ||
                            chapter.isCompleted
                          }
                        />
                      </div>
                    </div>
                    <div className="px-3">
                      <div
                        className={`w-fit rounded-md px-4 py-0.5 text-center font-medium ${
                          isLockedContent
                            ? "bg-[#E9E9E9] text-white dark:bg-[#232323] dark:text-[#979797]"
                            : isCurrentChapter
                              ? "bg-[#C3F8FF] text-primary dark:bg-[#446888] dark:text-[#A3D4FF]"
                              : "bg-primary text-white dark:bg-[#64A6E1] dark:text-[#0E0E0E]"
                        } `}
                      >
                        {chapter.fromPage} - {chapter.toPage}
                      </div>
                    </div>
                    <div
                      className={`px-3 pb-3 text-[15px] font-medium ${isLockedContent && "text-[#E9E9E9] dark:text-[#2E2E2E]"}`}
                    >
                      {chapter.title}
                    </div>
                  </Card>
                );
              })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const FullBookChaptersLoading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? <FullBookChaptersSkeleton /> : null;
};

export default FullBookChapters;
